import {
    ChangeDetectorRef,
    Component,
    HostListener,
    NgZone,
    OnInit
} from '@angular/core';
import {catchError, map} from 'rxjs/operators';
import {forkJoin, Observable, of} from 'rxjs';

import {LoadingLists} from '../../interfaces/LoadingLists.interfaces';
import {LoadingListService} from '../../services/loading-list.service';
import {TranslateModule} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';

import {Warehouse} from '../../interfaces/Warehouses.interfaces';
import {environment} from '../../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {ClientsBrands} from '../../services/routing.service';
import {NgSelectModule} from '@ng-select/ng-select';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Carrier} from '../../interfaces/anagraphic.interfaces';
import {AnagraphicService} from '../../services/anagraphic.service';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {CarrierResponse, SelectNewTourDialogComponent} from './select-new-tour-dialog/select-new-tour-dialog.component';
import {ApplyTransitDialogComponent, ParcelInTrip, TransitWarehouse} from './apply-transit-dialog/apply-transit-dialog.component';
import {RemoveFromTripDialogComponent} from './remove-from-trip-dialog/remove-from-trip-dialog.component';
import {ConfirmationPopupComponent} from '../../components/confirmation-popup/confirmation-popup.component';
import {ScrollTopButtonComponent} from '../../components/scroll-top-button/scroll-top-button.component';
import {NotificationService} from '../../services/notification.service';
import {ErrorHelperService} from '../../services/error-helper.service';
import FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import {NgxPaginationModule} from 'ngx-pagination';

interface ChangeTourRequest {
    comParcelsList: number[];
    tourRoutingId: number;
    warehouseCode: string | null;
    carrierCode?: string | null;
}

interface ChangeTourResponse {
    action: 'SUCCESS' | 'CONFIRM_REQUIRED';
    confirmationQuestion: string | null;
    tripGroups: string[];
    outcomes: string[];
    parcelsInTrip: ParcelInTrip[];
}

@Component({
    selector: 'app-change-tour',
    standalone: true,
    imports: [
        TranslateModule,
        CommonModule,
        RouterModule,
        FormsModule,
        NgSelectModule,
        MatTooltipModule,
        MatDialogModule,
        DragDropModule,
        NgxPaginationModule,
        ConfirmationPopupComponent,
        ScrollTopButtonComponent
    ],
    templateUrl: './change-tour.component.html',
    styleUrls: ['./change-tour.component.css']
})
export class ChangeTourComponent implements OnInit {

    isLoading = false;
    isSearchLoading = false;

    isFiltersCollapsed = false;

    warehouseCodes: string[] = [];
    warehouses: Warehouse[] = [];
    filterWarehouse: string | null = null;

    filterStatus = 'ALL_NO_CLOSED';
    filterType = '';

    searchLoadingListCode = '';
    filterParcelCode = '';
    filterWaybillCode = '';
    filterParcelDateFromStr = '';
    excelCodes = new Set<string>();
    excelFileName = '';
    excelNotFoundCodes: string[] = [];
    isExcelPending = false;
    isExcelClearing = false;

    includeClosed = false;

    carriers: Carrier[] = [];

    parcelsColumns: { key: string; label: string }[] = [];

    private readonly COLUMNS_STORAGE_KEY = 'changeTour_columnsOrder';

    dropdownStyles: { [key: string]: any } = {};
    openDropdownId: number | null = null;

    appliedWarehouse: string | null = null;

    selectedDateFrom: Date | null = null;
    selectedDateTo: Date | null = null;
    selectedDateFromStr = '';
    selectedDateToStr = '';

    lists: LoadingLists[] = [];
    filteredLists: LoadingLists[] = [];

    itemsPerPage = 25;
    currentPage = 1;
    pagedLists: LoadingLists[] = [];

    selectedIds = new Set<number>();

    isPrinting = false;
    isExporting = false;

    parcels: any[] = [];
    filteredParcels: any[] = [];
    pagedParcels: any[] = [];
    parcelsItemsPerPage = 25;
    parcelsCurrentPage = 1;
    selectedParcelIds = new Set<number>();
    isParcelsExporting = false;

    transitWarehouses: TransitWarehouse[] = [];
    transitCarriers: CarrierResponse[] = [];
    restoreTransitConfirmVisible = false;
    restoreTransitConfirmMessage = '';

    parcelsSortState = {active: 'parcelCode', direction: 'asc' as 'asc' | 'desc'};
    parcelsFiltersOpen: Record<string, boolean> = {};
    parcelsFilterSearch: Record<string, string> = {};
    parcelsFilterSelected: Record<string, Set<string>> = {};
    parcelsFilterValuesAll: Record<string, string[]> = {};
    parcelsFilterValuesShown: Record<string, string[]> = {};

    isDetailVisible = false;
    selectedLoadingList: LoadingLists | null = null;

    summaryTotal = 0;
    summaryDraft = 0;
    summaryValidated = 0;
    summaryClosed = 0;
    summaryArrived = 0;

    totDraftItems = 0;
    totDraftWeight = 0;
    totDraftVolume = 0;
    totPreparedItems = 0;
    totPreparedWeight = 0;
    totPreparedVolume = 0;
    totArrivedItems = 0;
    totArrivedWeight = 0;
    totArrivedVolume = 0;
    totClosedItems = 0;
    totClosedWeight = 0;
    totClosedVolume = 0;

    isReportCollapsed = false;

    sortState = {active: '', direction: 'asc' as 'asc' | 'desc'};

    filtersOpen: Record<string, boolean> = {};
    filterSearch: Record<string, string> = {};
    filterSelected: Record<string, Set<string>> = {};
    filterValuesAll: Record<string, string[]> = {};
    filterValuesShown: Record<string, string[]> = {};

    private filteredBaseLists: LoadingLists[] = [];

    constructor(
        private loadingListService: LoadingListService,
        private http: HttpClient,
        private anagraphicService: AnagraphicService,
        private dialog: MatDialog,
        private notificationService: NotificationService,
        private errorHelperService: ErrorHelperService,
        private cdr: ChangeDetectorRef,
        private ngZone: NgZone
    ) {
    }

    parcelsFilterPopupPosition = { top: '0px', left: '0px' };
    activeParcelsFilterKey: string | null = null;

    parcelsFilterValuesActive: Record<string, string[]> = {};

    private buildParcelsFilterValuesActive(): void {
        const cols = Object.keys(this.parcelsFilterValuesAll);
        const newActive: Record<string, string[]> = {};

        cols.forEach(col => {
            const filtered = this.parcels.filter(x => {
                return cols.every(c => {
                    if (c === col) return true;
                    const sel = this.parcelsFilterSelected[c];
                    if (!sel || sel.size === 0) return true;
                    const v = c === 'parcelDate'
                        ? this.formatDateTime(x.parcelDate)
                        : (x[c] ?? '').toString();
                    return sel.has(v);
                });
            });

            const uniq = (arr: string[]) => Array.from(new Set(arr.filter(v => v !== '')));
            newActive[col] = uniq(
                filtered.map(x => col === 'parcelDate'
                    ? this.formatDateTime(x.parcelDate)
                    : (x[col] ?? '').toString()
                )
            );
        });

        this.parcelsFilterValuesActive = { ...newActive };
    }


    ngOnInit(): void {
        this.parcelsColumns = this.loadColumnsOrder();
        this.loadWarehouses();

        const today = new Date();
        const todayStr = this.toYyyyMmDd(today);

        this.selectedDateFrom = today;
        this.selectedDateTo = today;
        this.selectedDateFromStr = todayStr;
        this.selectedDateToStr = todayStr;
        this.filterParcelDateFromStr = this.defaultParcelDateFrom();
    }

    private defaultParcelDateFrom(): string {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return this.toYyyyMmDd(date);
    }

    private hideLoaderAfterRender(): void {
        this.cdr.detectChanges();

        this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.ngZone.run(() => {
                        this.isLoading = false;
                        this.isSearchLoading = false;
                        this.cdr.detectChanges();
                        if (this.isExcelPending) {
                            setTimeout(() => this.isExcelPending = false, 150);
                        }
                    });
                });
            });
        });
    }

    private defaultColumns() {
        return [
            {key: 'parcelCode', label: 'Codice collo'},
            {key: 'parcelWaybillCode', label: 'Documento'},
            {key: 'parcelShipmentCode', label: 'Spedizione'},
            {key: 'dealerCode', label: 'Codice Concessionario'},
            {key: 'dealerName', label: 'Concessionario'},
            {key: 'carrierName', label: 'Vettore'},
            {key: 'tourRoutingCode', label: 'Tour'},
            {key: 'appliedTransitWarehouseName', label: 'Transito'},
            {key: 'tripCode', label: 'Viaggio'},
            {key: 'parcelDate', label: 'Data affido'}
        ];
    }

    private loadColumnsOrder() {
        try {
            const saved = localStorage.getItem(this.COLUMNS_STORAGE_KEY);
            if (saved) {
                const keys: string[] = JSON.parse(saved);
                const defaults = this.defaultColumns();

                const mapped = keys
                    .map(k => defaults.find(c => c.key === k))
                    .filter(c => !!c) as { key: string; label: string }[];

                defaults.forEach(d => {
                    if (!mapped.find(m => m.key === d.key)) mapped.push(d);
                });

                return mapped;
            }
        } catch {
        }

        return this.defaultColumns();
    }

    private saveColumnsOrder(): void {
        const keys = this.parcelsColumns.map(c => c.key);
        localStorage.setItem(this.COLUMNS_STORAGE_KEY, JSON.stringify(keys));
    }

    onColumnDrop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.parcelsColumns, event.previousIndex, event.currentIndex);
        this.saveColumnsOrder();
    }

    getColumnValue(p: any, key: string): string {
        if (key === 'parcelDate') return this.formatDateTime(p.parcelDate);
        return (p[key] ?? '').toString();
    }

    loadWarehouses(): void {
        this.http.get<Warehouse[]>(`${environment.apiUrl}/tracking/warehouses`).subscribe({
            next: (data) => {
                this.warehouses = data;

                const stored = localStorage.getItem('userFunctions');
                if (stored) {
                    const parsed = JSON.parse(stored);
                    this.warehouseCodes = parsed.warehouses;

                    if (this.warehouseCodes && this.warehouseCodes !== 'all' as any) {
                        this.warehouses = this.warehouses.filter(w =>
                            this.warehouseCodes.includes(w.warehouseCode)
                        );
                    }

                    if (this.warehouses.length === 1) {
                        this.filterWarehouse = this.warehouses[0].warehouseCode;
                    }
                }
            },
            error: (err) => {
                const message = this.errorHelperService.extractBackendMessage(err);
                this.notificationService.error(`Errore: ${message}`);
            }
        });
    }

    toggleFiltersCollapsed(): void {
        this.isFiltersCollapsed = !this.isFiltersCollapsed;
    }

    openSelectNewTourDialog(): void {
        const dialogRef = this.dialog.open(SelectNewTourDialogComponent, {
            width: '600px',
            maxWidth: '95vw',
            panelClass: 'app-popup-dialog',
            autoFocus: false,
            data: {warehouseCode: this.appliedWarehouse, comParcelsList: this.getSelectedComParcelIds()}
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (!result?.tourRoutingId) return;
            this.confirmChangeTour(result.tourRoutingId, result.carrierCode || null);
        });
    }

    openApplyTransitDialog(): void {
        const comParcelsList = this.getSelectedComParcelIds();
        if (!comParcelsList.length) {
            this.notificationService.warning('Seleziona almeno un collo');
            return;
        }

        const dialogRef = this.dialog.open(ApplyTransitDialogComponent, {
            width: '640px',
            maxWidth: '95vw',
            panelClass: 'app-popup-dialog',
            autoFocus: false,
            data: {
                warehouseCode: this.appliedWarehouse,
                comParcelsList,
                transitWarehouses: this.transitWarehouses,
                carriers: this.transitCarriers
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (!result?.transitWarehouseCode || !result?.carrierCode) return;
            this.confirmApplyTransit(comParcelsList, result.transitWarehouseCode, result.carrierCode);
        });
    }

    hasSelectedParcelsWithAppliedTransit(): boolean {
        return this.parcels.some(p => this.selectedParcelIds.has(p.parcelComplementaryId) && !!p.appliedTransitWarehouseCode);
    }

    openRestoreTransitConfirm(): void {
        const count = this.parcels.filter(p => this.selectedParcelIds.has(p.parcelComplementaryId) && !!p.appliedTransitWarehouseCode).length;
        if (!count) return;
        this.restoreTransitConfirmMessage = `Ripristinare il percorso originale per ${count} ${count === 1 ? 'collo' : 'colli'} con transito applicato?`;
        this.restoreTransitConfirmVisible = true;
    }

    cancelRestoreTransit(): void {
        this.restoreTransitConfirmVisible = false;
    }

    confirmRestoreTransit(): void {
        this.restoreTransitConfirmVisible = false;
        const comParcelsList = this.parcels
            .filter(p => this.selectedParcelIds.has(p.parcelComplementaryId) && !!p.appliedTransitWarehouseCode)
            .map(p => Number(p.parcelComplementaryId));
        if (!comParcelsList.length) return;

        this.postWithTripConfirmation(
            `${environment.apiUrl}/change-tour/restore-transit`,
            {comParcelsList, warehouseCode: this.appliedWarehouse},
            'Percorso originale ripristinato',
            'Errore durante il ripristino del percorso');
    }

    private confirmApplyTransit(comParcelsList: number[], transitWarehouseCode: string, carrierCode: string): void {
        this.postWithTripConfirmation(
            `${environment.apiUrl}/change-tour/apply-transit`,
            {comParcelsList, warehouseCode: this.appliedWarehouse, transitWarehouseCode, carrierCode},
            `Transito da ${this.getTransitWarehouseLabel(transitWarehouseCode)} applicato`,
            'Errore durante l\'applicazione del transito');
    }

    private postWithTripConfirmation(url: string, payload: object, successMessage: string, errorMessage: string): void {
        this.isLoading = true;
        this.http.post<ChangeTourResponse>(url, payload)
            .subscribe({
                next: (response) => {
                    if (response?.action === 'CONFIRM_REQUIRED') {
                        this.hideLoaderAfterRender();
                        this.dialog.open(RemoveFromTripDialogComponent, {
                            width: '640px',
                            maxWidth: '95vw',
                            panelClass: 'app-popup-dialog',
                            autoFocus: false,
                            data: {message: response.confirmationQuestion || '', tripGroups: response.tripGroups || [], outcomes: response.outcomes || [], parcelsInTrip: response.parcelsInTrip || []}
                        }).afterClosed().subscribe((confirmed) => {
                            if (confirmed) {
                                this.postWithTripConfirmation(url, {...payload, removeFromTripConfirmed: true}, successMessage, errorMessage);
                            }
                        });
                        return;
                    }
                    this.notificationService.success(successMessage);
                    this.selectedParcelIds.clear();
                    this.reloadParcels();
                },
                error: (err) => {
                    const message = this.errorHelperService.extractBackendMessage?.(err) || errorMessage;
                    this.notificationService.error(message);
                    this.hideLoaderAfterRender();
                }
            });
    }

    private getSelectedComParcelIds(): number[] {
        return this.parcels
            .filter(p => this.selectedParcelIds.has(p.parcelComplementaryId))
            .map(p => Number(p.parcelComplementaryId));
    }

    private getTransitWarehouseLabel(code?: string | null): string {
        if (!code) return '';
        const warehouse = this.transitWarehouses.find(w => w.warehouseCode === code)
            || this.warehouses.find(w => w.warehouseCode === code);
        return warehouse ? (warehouse.warehouseShortName || warehouse.warehouseName || code) : code;
    }

    private decorateParcels(rows: any[]): any[] {
        return (Array.isArray(rows) ? rows : []).map(p => ({
            ...p,
            appliedTransitWarehouseName: this.getTransitWarehouseLabel(p.appliedTransitWarehouseCode)
        }));
    }

    private fetchTransitWarehouses(warehouseCode: string): Observable<TransitWarehouse[]> {
        return this.http.get<TransitWarehouse[]>(`${environment.apiUrl}/change-tour/transit-warehouses`, {params: {warehouseCode}});
    }

    private fetchTransitCarriers(warehouseCode: string): Observable<CarrierResponse[]> {
        return this.http.post<{ carriers: CarrierResponse[] }>(`${environment.apiUrl}/change-tour/apply-transit/carriers`, {warehouseCode})
            .pipe(map(response => Array.isArray(response?.carriers) ? response.carriers : []));
    }


    private toYyyyMmDd(date: Date): string {
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    private fromYyyyMmDd(value: string): Date | null {
        if (!value) return null;

        const [y, m, d] = value.split('-').map(Number);
        if (!y || !m || !d) return null;

        return new Date(y, m - 1, d);
    }



    private syncDatesFromDateStrings(): void {
        const from = this.fromYyyyMmDd(this.selectedDateFromStr);
        const to = this.fromYyyyMmDd(this.selectedDateToStr);

        if (from) this.selectedDateFrom = from;
        if (to) this.selectedDateTo = to;
    }


    resetFilters(): void {
        this.filterWarehouse = this.warehouses.length === 1 ? this.warehouses[0].warehouseCode : null;
        this.filterStatus = 'ALL_NO_CLOSED';
        this.filterType = '';
        this.searchLoadingListCode = '';
        this.filterParcelCode = '';
        this.filterWaybillCode = '';
        this.filterParcelDateFromStr = this.defaultParcelDateFrom();
        this.excelCodes = new Set<string>();
        this.excelFileName = '';
        this.excelNotFoundCodes = [];

        const today = new Date();
        this.selectedDateFrom = today;
        this.selectedDateTo = today;
        this.selectedDateFromStr = this.toYyyyMmDd(today);
        this.selectedDateToStr = this.toYyyyMmDd(today);

        this.currentPage = 1;
        this.selectedIds.clear();
        this.selectedParcelIds.clear();

        this.clearLists();
        this.clearParcels();
        this.updatePagedLists();
        this.updateSummary();
    }

    searchWarehouseFn(term: string, item: any): boolean {
        const t = term.toLowerCase();
        return (
            item.warehouseName?.toLowerCase().includes(t) ||
            item.warehouseShortName?.toLowerCase().includes(t) ||
            item.warehouseCode?.toLowerCase().includes(t)
        );
    }

    search(): void {
        this.syncDatesFromDateStrings();

        if (!this.filterWarehouse) {
            this.notificationService.warning('Seleziona un magazzino per avviare la ricerca');

            this.clearLists();
            this.clearParcels();

            this.updatePagedLists();
            this.updateSummary();

            this.isLoading = false;
            this.isSearchLoading = false;
            this.isExcelPending = false;
            return;
        }

        this.appliedWarehouse = this.filterWarehouse;

        if (this.filterParcelCode.trim() || this.filterWaybillCode.trim()) {
            this.excelCodes = new Set<string>();
            this.excelFileName = '';
        }

        this.isSearchLoading = true;
        this.isLoading = true;

        this.selectedIds.clear();
        this.selectedParcelIds.clear();
        this.currentPage = 1;
        this.parcelsCurrentPage = 1;

        let clientsBrands: ClientsBrands[] = [];

        const stored = localStorage.getItem('userFunctions');
        if (stored) {
            const parsed = JSON.parse(stored);
            clientsBrands = parsed.clients;
        }

        const payload: any = {
            warehouseCode: this.filterWarehouse || null,
            status: this.filterStatus || null,
            dateStart: this.selectedDateFromStr || null,
            dateEnd: this.selectedDateToStr || null,
            clients: clientsBrands,
            loadingListCode: this.searchLoadingListCode || null
        };

        forkJoin({
            lists: this.loadingListService.searchLoadingLists(payload).pipe(
                catchError((err) => {
                    const message = this.errorHelperService.extractBackendMessage(err);
                    this.notificationService.error(`Errore: ${message}`);
                    return of([] as LoadingLists[]);
                })
            ),
            parcels: this.fetchParcels(this.filterWarehouse).pipe(
                catchError((err) => {
                    const message = this.errorHelperService.extractBackendMessage?.(err) || 'Errore di rete';
                    this.notificationService.error(`Cambio Tour: errore chiamata /search: ${message}`);
                    return of([] as any[]);
                })
            ),
            transitWarehouses: this.fetchTransitWarehouses(this.filterWarehouse).pipe(
                catchError(() => of([] as TransitWarehouse[]))
            ),
            transitCarriers: this.fetchTransitCarriers(this.filterWarehouse).pipe(
                catchError(() => of([] as CarrierResponse[]))
            )
        }).subscribe({
            next: ({lists, parcels, transitWarehouses, transitCarriers}) => {
                this.lists = (lists || []).map(ll => ({
                    ...ll,
                    uiType: this.getLoadingListType(ll)
                }));

                this.applyFrontendFilters();

                this.transitWarehouses = Array.isArray(transitWarehouses) ? transitWarehouses : [];
                this.transitCarriers = transitCarriers;
                this.parcels = this.decorateParcels(parcels);
                this.selectedParcelIds.clear();
                this.buildParcelsFilterValues(this.parcels);
                this.applyParcelsAll();

                this.hideLoaderAfterRender();
            },
            error: () => {
                this.clearLists();
                this.clearParcels();
                this.hideLoaderAfterRender();
            }
        });
    }

    private clearLists(): void {
        this.lists = [];
        this.filteredLists = [];
        this.filteredBaseLists = [];
        this.pagedLists = [];
        this.selectedIds.clear();
        this.currentPage = 1;
    }

    private clearParcels(): void {
        this.parcels = [];
        this.filteredParcels = [];
        this.pagedParcels = [];
        this.selectedParcelIds.clear();
        this.parcelsCurrentPage = 1;
    }

    private fetchParcels(warehouseCode: string): Observable<any[]> {
        const body = {
            warehouseCode,
            dealerCode: (this.searchLoadingListCode || '').trim() || null,
            parcelCode: (this.filterParcelCode || '').trim() || null,
            waybillCode: (this.filterWaybillCode || '').trim() || null,
            codes: Array.from(this.excelCodes),
            parcelDateFrom: this.filterParcelDateFromStr || null
        };

        return this.http.post<any[]>(`${environment.apiUrl}/change-tour/search`, body);
    }

    private reloadParcels(): void {
        this.parcelsCurrentPage = 1;

        if (!this.filterWarehouse) {
            this.clearParcels();
            this.hideLoaderAfterRender();
            return;
        }

        this.isLoading = true;

        this.fetchParcels(this.filterWarehouse).subscribe({
            next: (rows) => {
                this.parcels = this.decorateParcels(rows);
                this.selectedParcelIds.clear();
                this.buildParcelsFilterValues(this.parcels);
                this.applyParcelsAll();

                this.hideLoaderAfterRender();
            },
            error: (err) => {
                const message = this.errorHelperService.extractBackendMessage?.(err) || 'Errore di rete';
                this.notificationService.error(`Cambio Tour: errore chiamata /search: ${message}`);

                this.clearParcels();
                this.hideLoaderAfterRender();
            }
        });
    }

    applyFrontendFilters(): void {
        const type = this.filterType ?? null;

        const base = this.lists.filter(ll => {
            const llType = (ll as any).uiType ?? this.getLoadingListType(ll);
            return !type || llType === type;
        });

        this.filteredBaseLists = base;

        this.buildFilterValues(this.filteredBaseLists);

        this.applyAll();
    }

    private updatePagedLists(): void {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        const end = start + this.itemsPerPage;
        this.pagedLists = this.filteredLists.slice(start, end);
    }

    setParcelsSort(col: string): void {
        if (this.parcelsSortState.active === col) {
            this.parcelsSortState.direction = this.parcelsSortState.direction === 'asc' ? 'desc' : 'asc';
        } else {
            this.parcelsSortState.active = col;
            this.parcelsSortState.direction = 'asc';
        }

        this.parcelsCurrentPage = 1;
        this.applyParcelsAll();
    }

    getParcelsSortIcon(col: string): string {
        if (this.parcelsSortState.active !== col) return 'bi-arrow-down-up';
        return this.parcelsSortState.direction === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
    }

    toggleParcelsFilterPopup(col: string, event: MouseEvent): void {
        event.stopPropagation();
        if (this.activeParcelsFilterKey === col) {
            this.activeParcelsFilterKey = null;
            return;
        }
        const target = event.target as HTMLElement;
        const th = target.closest('th')!;
        const rect = th.getBoundingClientRect();
        this.parcelsFilterPopupPosition = {
            top: rect.bottom + 4 + 'px',
            left: rect.left + 'px'
        };
        this.buildParcelsFilterValuesActive();
        this.rebuildFilterList(col);
        this.activeParcelsFilterKey = col;
    }

    closeAllParcelsPopups(): void {
        this.activeParcelsFilterKey = null;
    }

    rebuildFilterList(col: string): void {
        const s = (this.parcelsFilterSearch[col] || '').trim().toLowerCase();
        const active = this.parcelsFilterValuesActive[col] || this.parcelsFilterValuesAll[col] || [];

        this.parcelsFilterValuesShown[col] = !s
            ? active
            : active.filter(v => v.toLowerCase().includes(s));
    }

    toggleParcelsFilterValue(col: string, value: string, checked: boolean): void {
        if (!this.parcelsFilterSelected[col]) this.parcelsFilterSelected[col] = new Set<string>();

        if (checked) this.parcelsFilterSelected[col].add(value);
        else this.parcelsFilterSelected[col].delete(value);
    }

    isParcelsValueSelected(col: string, value: string): boolean {
        return this.parcelsFilterSelected[col]?.has(value) ?? false;
    }

    onParcelsToggleAll(col: string, checked: boolean): void {
        if (!this.parcelsFilterSelected[col]) this.parcelsFilterSelected[col] = new Set<string>();

        this.parcelsFilterSelected[col].clear();

        if (checked) {
            (this.parcelsFilterValuesAll[col] || []).forEach(v => this.parcelsFilterSelected[col].add(v));
        }
    }

    resetParcelsFilter(col: string): void {
        this.parcelsFilterSelected[col] = new Set<string>();
        this.parcelsFilterSearch[col] = '';
        this.rebuildFilterList(col);
        this.parcelsCurrentPage = 1;
        this.activeParcelsFilterKey = null;
        this.applyParcelsAll();
    }
    applyParcelsFilter(): void {
        this.parcelsCurrentPage = 1;
        this.buildParcelsFilterValuesActive();
        this.applyParcelsAll();
        this.activeParcelsFilterKey = null;
    }

    resetAllParcelsFilters(): void {
        const cols = Object.keys(this.parcelsFilterValuesAll).length > 0
            ? Object.keys(this.parcelsFilterValuesAll)
            : [
                'parcelCode',
                'parcelWaybillCode',
                'parcelShipmentCode',
                'dealerCode',
                'dealerName',
                'carrierName',
                'tourRoutingCode',
                'appliedTransitWarehouseName',
                'tripCode',
                'parcelDate'
            ];

        cols.forEach(col => {
            this.parcelsFilterSelected[col] = new Set<string>();
            this.parcelsFilterSearch[col] = '';
            this.rebuildFilterList(col);
            this.parcelsFiltersOpen[col] = false;
        });

        this.parcelsCurrentPage = 1;
        this.applyParcelsAll();
    }

    private buildParcelsFilterValues(source: any[]): void {
        const cols = [
            'parcelCode',
            'parcelWaybillCode',
            'parcelShipmentCode',
            'dealerCode',
            'dealerName',
            'carrierName',
            'tourRoutingCode',
            'appliedTransitWarehouseName',
            'tripCode',
            'parcelDate'
        ];

        cols.forEach(col => {
            if (!this.parcelsFilterSelected[col]) this.parcelsFilterSelected[col] = new Set<string>();
            if (!this.parcelsFilterSearch[col]) this.parcelsFilterSearch[col] = '';
            if (!this.parcelsFilterValuesAll[col]) this.parcelsFilterValuesAll[col] = [];
            if (!this.parcelsFilterValuesShown[col]) this.parcelsFilterValuesShown[col] = [];
        });

        const uniq = (arr: string[]) => Array.from(new Set(arr.filter(x => x != null && x !== '')));

        this.parcelsFilterValuesAll['parcelCode'] = uniq(source.map(x => (x.parcelCode ?? '').toString()));
        this.parcelsFilterValuesAll['parcelWaybillCode'] = uniq(source.map(x => (x.parcelWaybillCode ?? '').toString())).sort();
        this.parcelsFilterValuesAll['parcelShipmentCode'] = uniq(source.map(x => (x.parcelShipmentCode ?? '').toString())).sort();
        this.parcelsFilterValuesAll['dealerCode'] = uniq(source.map(x => (x.dealerCode ?? '').toString())).sort();
        this.parcelsFilterValuesAll['dealerName'] = uniq(source.map(x => (x.dealerName ?? '').toString())).sort();
        this.parcelsFilterValuesAll['carrierName'] = uniq(source.map(x => (x.carrierName ?? '').toString())).sort();
        this.parcelsFilterValuesAll['tourRoutingCode'] = uniq(source.map(x => (x.tourRoutingCode ?? '').toString())).sort();
        this.parcelsFilterValuesAll['appliedTransitWarehouseName'] = uniq(source.map(x => (x.appliedTransitWarehouseName ?? '').toString())).sort();
        this.parcelsFilterValuesAll['tripCode'] = uniq(source.map(x => (x.tripCode ?? '').toString())).sort();
        this.parcelsFilterValuesAll['parcelDate'] = uniq(source.map(x => this.formatDateTime(x.parcelDate))).sort();

        Object.keys(this.parcelsFilterValuesAll).forEach(col => this.rebuildFilterList(col));
    }

    private applyParcelsAll(): void {
        this.buildParcelsFilterValuesActive();
        const cols = [
            'parcelCode',
            'parcelWaybillCode',
            'parcelShipmentCode',
            'dealerCode',
            'dealerName',
            'carrierName',
            'tourRoutingCode',
            'appliedTransitWarehouseName',
            'tripCode',
            'parcelDate'
        ];

        const matches = (x: any, col: string) => {
            const sel = this.parcelsFilterSelected[col];
            if (!sel || sel.size === 0) return true;

            const v = col === 'parcelDate'
                ? this.formatDateTime(x.parcelDate)
                : (x[col] ?? '').toString();

            return sel.has(v);
        };

        this.updateExcelNotFoundCodes();
        this.filteredParcels = this.parcels.filter(x => cols.every(c => matches(x, c)));

        const dir = this.parcelsSortState.direction === 'asc' ? 1 : -1;
        const col = this.parcelsSortState.active;

        this.filteredParcels = this.filteredParcels.slice().sort((a, b) => {
            if (col === 'parcelDate') {
                const ta = new Date(a.parcelDate as any).getTime() || 0;
                const tb = new Date(b.parcelDate as any).getTime() || 0;

                if (ta < tb) return -1 * dir;
                if (ta > tb) return 1 * dir;
                return 0;
            }

            const va = (a[col] ?? '').toString().toLowerCase();
            const vb = (b[col] ?? '').toString().toLowerCase();

            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });

        const start = (this.parcelsCurrentPage - 1) * this.parcelsItemsPerPage;
        const end = start + this.parcelsItemsPerPage;
        this.pagedParcels = this.filteredParcels.slice(start, end);
    }

    async onExcelFileChange(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        input.value = '';
        if (!file) return;

        try {
            const codes = await this.readExcelCodes(file);
            if (!codes.length) {
                this.notificationService.warning('Il file non contiene codici');
                return;
            }

            this.excelCodes = new Set(codes);
            this.excelFileName = file.name;
            this.filterParcelCode = '';
            this.filterWaybillCode = '';
            this.isExcelPending = true;
            this.search();
        } catch {
            this.notificationService.error('Impossibile leggere il file Excel');
        }
    }

    clearExcelCodes(): void {
        if (this.isExcelClearing) return;

        this.isExcelClearing = true;
        setTimeout(() => {
            this.excelCodes = new Set<string>();
            this.excelFileName = '';
            this.excelNotFoundCodes = [];
            this.isExcelClearing = false;
            this.clearLists();
            this.clearParcels();
            this.updatePagedLists();
            this.updateSummary();
        }, 300);
    }

    getExcelTooltip(): string {
        return this.excelNotFoundCodes.length && this.excelNotFoundCodes.length <= 10
            ? `${this.excelFileName}. Non trovati: ${this.excelNotFoundCodes.join(', ')}`
            : this.excelFileName;
    }

    downloadExcelNotFoundCodes(): void {
        if (!this.excelNotFoundCodes.length) return;

        const ws = XLSX.utils.json_to_sheet(this.excelNotFoundCodes.map(code => ({'Codice': code})));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Non trovati');

        const wbout = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
        FileSaver.saveAs(new Blob([wbout], {type: 'application/octet-stream'}), `codici_non_trovati_${this.nowStamp()}.xlsx`);
    }

    private async readExcelCodes(file: File): Promise<string[]> {
        const workbook = XLSX.read(new Uint8Array(await file.arrayBuffer()), {type: 'array'});
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!sheet || !sheet['!ref']) return [];

        const range = XLSX.utils.decode_range(sheet['!ref']);
        const codes = new Set<string>();

        for (let r = range.s.r; r <= range.e.r; r++) {
            for (let c = range.s.c; c <= range.e.c; c++) {
                const cell: XLSX.CellObject | undefined = sheet[XLSX.utils.encode_cell({r, c})];
                if (!cell || cell.v == null || cell.v === '') continue;

                const text = cell.t === 'n' && /e/i.test(cell.w ?? '') ? String(cell.v) : (cell.w ?? String(cell.v));
                const code = text.trim().toUpperCase();
                if (code) codes.add(code);
            }
        }

        return Array.from(codes);
    }

    private updateExcelNotFoundCodes(): void {
        if (!this.excelCodes.size) {
            this.excelNotFoundCodes = [];
            return;
        }

        const known = new Set<string>();
        this.parcels.forEach(p => {
            if (p.parcelCode) known.add(p.parcelCode.toString().toUpperCase());
            if (p.parcelWaybillCode) known.add(p.parcelWaybillCode.toString().toUpperCase());
        });
        this.excelNotFoundCodes = Array.from(this.excelCodes).filter(code => !known.has(code));
    }

    exportSelectedParcels(): void {
        if (this.selectedParcelIds.size === 0 || this.isParcelsExporting) return;

        this.isParcelsExporting = true;

        try {
            const rows = this.filteredParcels.filter(p => this.selectedParcelIds.has(p.parcelComplementaryId));

            const data = rows.map(p => ({
                'Codice collo': p.parcelCode ?? '',
                'Codice Concessionario': p.dealerCode ?? '',
                'Concessionario': p.dealerName ?? '',
                'Vettore': p.carrierName ?? '',
                'Tour': p.tourRoutingCode ?? '',
                'Transito': p.appliedTransitWarehouseName ?? '',
                'Viaggio': p.tripCode ?? '',
                'Data affido': this.formatDateTime(p.parcelDate)
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(wb, ws, 'Colli');

            const wbout = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
            const blob = new Blob([wbout], {type: 'application/octet-stream'});

            const now = new Date();
            const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;

            FileSaver.saveAs(blob, `colli_selezionati_${ts}.xlsx`);
        } catch {
            this.notificationService?.error?.('Errore durante l\'esportazione Excel');
        } finally {
            this.isParcelsExporting = false;
        }
    }

    setParcelsItemsPerPage(n: number): void {
        this.parcelsItemsPerPage = n;
        this.parcelsCurrentPage = 1;
        this.applyParcelsAll();
    }

    onParcelsPageChange(page: number): void {
        const total = Math.max(1, Math.ceil(this.filteredParcels.length / this.parcelsItemsPerPage));

        if (page < 1 || page > total) return;

        this.parcelsCurrentPage = page;
        this.applyParcelsAll();
    }

    getParcelsTotalPages(): number {
        return Math.max(1, Math.ceil(this.filteredParcels.length / this.parcelsItemsPerPage));
    }

    getParcelsPageNumbers(): number[] {
        const total = this.getParcelsTotalPages();
        return Array.from({length: total}, (_, i) => i + 1);
    }

    trackByParcelId(index: number, p: any): number {
        return p?.parcelComplementaryId ?? index;
    }

    toggleParcelSelection(id: number): void {
        if (this.selectedParcelIds.has(id)) this.selectedParcelIds.delete(id);
        else this.selectedParcelIds.add(id);
    }

    isParcelSelected(id: number): boolean {
        return this.selectedParcelIds.has(id);
    }

    selectAllParcels(): void {
        this.filteredParcels.forEach(x => this.selectedParcelIds.add(x.parcelComplementaryId));
    }

    deselectAllParcels(): void {
        this.selectedParcelIds.clear();
    }

    hasSelectedParcels(): boolean {
        return this.selectedParcelIds.size > 0;
    }

    areAllParcelsSelected(): boolean {
        return this.filteredParcels.length > 0
            && this.filteredParcels.every(x => this.selectedParcelIds.has(x.parcelComplementaryId));
    }

    private buildColliColumns(ll: any): {
        label: string;
        shown: number;
        total: number;
        pct: number;
        kg: number | string;
        m3: number | string;
    } {
        const label = ll.uiType === 'ARRIVAL' ? 'SCARICATI' : 'CARICATI';

        const shown = this.getShownCount(ll) ?? 0;
        const total = ll.loadingListItemsCount ?? 0;
        const pct = total > 0 ? this.getPercent(shown, total) : 0;

        const kg = this.round2(this.getShownWeight(ll));
        const m3 = this.round3(this.getShownVolume(ll));

        return {label, shown, total, pct, kg, m3};
    }

    private round2(v: any): number | string {
        const n = Number(v);
        return isFinite(n) ? Math.round(n * 100) / 100 : '';
    }

    private round3(v: any): number | string {
        const n = Number(v);
        return isFinite(n) ? Math.round(n * 1000) / 1000 : '';
    }

    public formatDateTime(value: any): string {
        const d = new Date(value);

        if (isNaN(d.getTime())) return '';

        const pad = (n: number) => n.toString().padStart(2, '0');

        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear().toString().slice(2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    private downloadBlob(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');

        a.href = url;
        a.download = filename;
        a.click();

        window.URL.revokeObjectURL(url);
    }

    private nowStamp(): string {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const hh = String(d.getHours()).padStart(2, '0');
        const mi = String(d.getMinutes()).padStart(2, '0');

        return `${yyyy}${mm}${dd}_${hh}${mi}`;
    }

    getLoadingListStatusLabel(status?: string): string {
        switch ((status || '').toUpperCase()) {
            case 'DRAFT':
                return 'BOZZA';
            case 'PREPARED':
                return 'PREPARATO';
            case 'ARRIVED':
                return 'ARRIVATO';
            case 'ARRIVING':
                return 'IN ARRIVO';
            case 'CLOSED':
                return 'CHIUSO';
            case 'DELETED':
                return 'ELIMINATO';
            default:
                return status || 'UNKNOWN';
        }
    }

    getLoadingListType(ll: any): 'DEPARTURE' | 'ARRIVAL' {
        const wh = (ll?.loadingListWarehouseCode || '').toString().trim().toLowerCase();
        const filtered = (this.appliedWarehouse || '').toString().trim().toLowerCase();

        return wh && filtered && wh === filtered ? 'DEPARTURE' : 'ARRIVAL';
    }

    getLoadingListTypeMeta(ll: any): { label: string; icon: string; color: string; bg: string } {
        const type = this.getLoadingListType(ll);

        if (type === 'DEPARTURE') {
            return {
                label: 'PARTENZA',
                icon: 'fa-arrow-up',
                color: '#6f42c1',
                bg: 'rgba(111, 66, 193, 0.12)'
            };
        }

        return {
            label: 'ARRIVO',
            icon: 'fa-arrow-down',
            color: '#20c997',
            bg: 'rgba(32, 201, 151, 0.12)'
        };
    }

    getWarehouseName(code?: string): string {
        if (!code) return '';

        const wh = this.warehouses.find(w => w.warehouseCode === code);

        return wh ? (wh.warehouseName ?? wh.warehouseCode) : code;
    }

    getWarehouseShortName(code?: string): string {
        if (!code) return '';

        const wh = this.warehouses.find(w => w.warehouseCode === code);

        return wh ? (wh.warehouseShortName ?? wh.warehouseCode) : code;
    }

    formatRatio(done?: number, total?: number): string {
        const d = done ?? 0;
        const t = total ?? 0;

        return `${d}/${t}`;
    }

    hasLoadedMetrics(ll: LoadingLists): boolean {
        return (ll.loadingListLoadedWeight ?? 0) > 0 || (ll.loadingListLoadedVolume ?? 0) > 0;
    }

    hasUnloadedMetrics(ll: LoadingLists): boolean {
        return (ll.loadingListUnloadedWeight ?? 0) > 0 || (ll.loadingListUnloadedVolume ?? 0) > 0;
    }

    formatKg(value?: number): string {
        const v = value ?? 0;
        return `${v.toLocaleString('it-IT', {maximumFractionDigits: 1, minimumFractionDigits: 0})} kg`;
    }

    formatM3(value?: number): string {
        const v = value ?? 0;
        return `${v.toLocaleString('it-IT', {maximumFractionDigits: 2, minimumFractionDigits: 0})} m³`;
    }

    getPercent(done?: number, total?: number): number {
        const d = done ?? 0;
        const t = total ?? 0;

        if (t <= 0) return 0;

        return Math.max(0, Math.min(100, Math.round((d / t) * 100)));
    }

    private metricForCard(ll: LoadingLists): { items: number; weight: number; volume: number } {
        const status = (ll.loadingListStatus ?? '').toUpperCase();

        const itemsCount = ll.loadingListItemsCount ?? 0;

        const loadedItems = ll.loadingListLoadedCount ?? 0;
        const loadedWeight = ll.loadingListLoadedWeight ?? 0;
        const loadedVolume = ll.loadingListLoadedVolume ?? 0;

        const unloadedItems = ll.loadingListUnloadedCount ?? 0;
        const unloadedWeight = ll.loadingListUnloadedWeight ?? 0;
        const unloadedVolume = ll.loadingListUnloadedVolume ?? 0;

        if (status === 'DRAFT') {
            const pendingItems = Math.max(0, itemsCount - loadedItems);
            return {items: pendingItems, weight: 0, volume: 0};
        }

        if (status === 'PREPARED') {
            return {items: loadedItems, weight: loadedWeight, volume: loadedVolume};
        }

        if (status === 'ARRIVED' || status === 'ARRIVING') {
            return {items: unloadedItems, weight: unloadedWeight, volume: unloadedVolume};
        }

        if (status === 'CLOSED') {
            return {items: unloadedItems, weight: unloadedWeight, volume: unloadedVolume};
        }

        return {items: 0, weight: 0, volume: 0};
    }

    updateSummary(): void {
        const list = this.filteredLists ?? [];
        const s = (v?: string) => (v ?? '').toUpperCase();

        const sumMetric = (arr: LoadingLists[]) => arr.reduce(
            (acc, x) => {
                const m = this.metricForCard(x);
                acc.items += m.items;
                acc.weight += m.weight;
                acc.volume += m.volume;
                return acc;
            },
            {items: 0, weight: 0, volume: 0}
        );

        this.summaryTotal = list.length;
        this.summaryDraft = list.filter(x => s(x.loadingListStatus) === 'DRAFT').length;
        this.summaryValidated = list.filter(x => s(x.loadingListStatus) === 'PREPARED').length;
        this.summaryArrived = list.filter(x => s(x.loadingListStatus) === 'ARRIVED' || s(x.loadingListStatus) === 'ARRIVING').length;
        this.summaryClosed = list.filter(x => s(x.loadingListStatus) === 'CLOSED').length;

        const d = sumMetric(list.filter(x => s(x.loadingListStatus) === 'DRAFT'));
        this.totDraftItems = d.items;
        this.totDraftWeight = d.weight;
        this.totDraftVolume = d.volume;

        const p = sumMetric(list.filter(x => s(x.loadingListStatus) === 'PREPARED'));
        this.totPreparedItems = p.items;
        this.totPreparedWeight = p.weight;
        this.totPreparedVolume = p.volume;

        const a = sumMetric(list.filter(x => s(x.loadingListStatus) === 'ARRIVED' || s(x.loadingListStatus) === 'ARRIVING'));
        this.totArrivedItems = a.items;
        this.totArrivedWeight = a.weight;
        this.totArrivedVolume = a.volume;

        const c = sumMetric(list.filter(x => s(x.loadingListStatus) === 'CLOSED'));
        this.totClosedItems = c.items;
        this.totClosedWeight = c.weight;
        this.totClosedVolume = c.volume;
    }

    toggleReportCollapsed(): void {
        this.isReportCollapsed = !this.isReportCollapsed;
    }

    getCarrierName(code?: string): string {
        if (!code) return '';

        const c = this.carriers.find(c => c.carrierCode === code);

        return c ? c.carrierName : code;
    }

    isArrived(status: any): boolean {
        return status === 'ARRIVED' || status === 'ARRIVATO' || status === 'ARRIVING';
    }

    getShownCount(ll: any): number {
        return ll.uiType === 'ARRIVAL'
            ? (ll.loadingListUnloadedCount ?? 0)
            : (ll.loadingListLoadedCount ?? 0);
    }

    getShownWeight(ll: any): number {
        return ll.uiType === 'ARRIVAL'
            ? (ll.loadingListUnloadedWeight ?? 0)
            : (ll.loadingListLoadedWeight ?? 0);
    }

    getShownVolume(ll: any): number {
        return ll.uiType === 'ARRIVAL'
            ? (ll.loadingListUnloadedVolume ?? 0)
            : (ll.loadingListLoadedVolume ?? 0);
    }

    isDropdownOpen(id: string | number): boolean {
        return this.openDropdownId === id;
    }

    closeAllDropdowns(): void {
        this.openDropdownId = null;
    }

    @HostListener('document:click')
    onDocumentClick(): void {
        this.closeAllDropdowns();
        this.activeParcelsFilterKey = null;
    }

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.closeAllDropdowns();
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.closeAllDropdowns();
    }

    setSort(col: string): void {
        if (this.sortState.active === col) {
            this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortState.active = col;
            this.sortState.direction = 'asc';
        }

        this.applyAll();
    }

    applyFilter(col: string): void {
        this.applyAll();
        this.filtersOpen[col] = false;
    }

    private getCreatedByUi(ll: LoadingLists): string {
        return ll.loadingListCreatedBy === 'SPARE PARTS' ? 'Spare Parts' : 'Atlas';
    }

    private initFilterState(cols: string[]): void {
        cols.forEach(col => {
            if (!this.filterSelected[col]) this.filterSelected[col] = new Set<string>();
            if (!this.filterSearch[col]) this.filterSearch[col] = '';
            if (!this.filterValuesAll[col]) this.filterValuesAll[col] = [];
            if (!this.filterValuesShown[col]) this.filterValuesShown[col] = [];
            if (!this.filtersOpen[col]) this.filtersOpen[col] = false;
        });
    }

    buildFilterValues(source: LoadingLists[]): void {
        const uniq = (arr: string[]) =>
            Array.from(new Set(arr.map(x => (x ?? '').toString().trim()).filter(x => x !== ''))).sort();

        const cols = ['type', 'code', 'createdByUi', 'carrier', 'plate', 'warehouse', 'status', 'extra', 'ptv'];

        this.initFilterState(cols);

        const map: Record<string, (ll: LoadingLists) => string> = {
            type: (ll) => this.getLoadingListTypeMeta(ll).label,
            code: (ll) => (ll.loadingListCode ?? '').toString(),
            createdByUi: (ll) => this.getCreatedByUi(ll),
            carrier: (ll) => this.getCarrierName(ll.loadingListCarrierCode) || '',
            plate: (ll) => (ll.loadingListPlateNo ?? '').toString(),
            warehouse: (ll) => this.getWarehouseName(ll.loadingListWarehouseCode) || '',
            status: (ll) => this.getLoadingListStatusLabel(ll.loadingListStatus) || '',
            extra: (ll) => (ll.loadingListIsExtra === 'Y' ? 'Sì' : 'No'),
            ptv: (ll) => (!ll.loadingListIsInternal ? 'Sì' : 'No')
        };

        cols.forEach(col => {
            this.filterValuesAll[col] = uniq(source.map(map[col]));
            this.filterValuesShown[col] = [...this.filterValuesAll[col]];
        });
    }

    applyAll(): void {
        let arr = [...this.filteredBaseLists];

        const getVal: Record<string, (ll: LoadingLists) => string> = {
            type: (ll) => this.getLoadingListTypeMeta(ll).label,
            code: (ll) => (ll.loadingListCode ?? '').toString(),
            createdByUi: (ll) => this.getCreatedByUi(ll),
            carrier: (ll) => this.getCarrierName(ll.loadingListCarrierCode) || '',
            plate: (ll) => (ll.loadingListPlateNo ?? '').toString(),
            warehouse: (ll) => this.getWarehouseName(ll.loadingListWarehouseCode) || '',
            status: (ll) => this.getLoadingListStatusLabel(ll.loadingListStatus) || '',
            extra: (ll) => (ll.loadingListIsExtra === 'Y' ? 'Sì' : 'No'),
            ptv: (ll) => (!ll.loadingListIsInternal ? 'Sì' : 'No')
        };

        Object.keys(getVal).forEach(col => {
            const sel = this.filterSelected[col];

            if (sel && sel.size > 0) {
                arr = arr.filter(ll => sel.has(getVal[col](ll)));
            }
        });

        if (this.sortState.active) {
            const col = this.sortState.active;
            const dir = this.sortState.direction;

            const getter: Record<string, (ll: LoadingLists) => any> = {
                type: (ll) => this.getLoadingListTypeMeta(ll).label,
                code: (ll) => ll.loadingListCode ?? '',
                createdAt: (ll) => ll.loadingListCreatedAt ?? null,
                createdByUi: (ll) => this.getCreatedByUi(ll),
                carrier: (ll) => this.getCarrierName(ll.loadingListCarrierCode) || '',
                plate: (ll) => ll.loadingListPlateNo ?? '',
                warehouse: (ll) => this.getWarehouseName(ll.loadingListWarehouseCode) || '',
                status: (ll) => this.getLoadingListStatusLabel(ll.loadingListStatus) || '',
                extra: (ll) => (ll.loadingListIsExtra === 'Y' ? 1 : 0),
                ptv: (ll) => (!ll.loadingListIsInternal ? 1 : 0)
            };

            const g = getter[col] || ((x: any) => x[col]);

            arr.sort((a, b) => {
                const va = g(a);
                const vb = g(b);

                if (va == null && vb == null) return 0;
                if (va == null) return dir === 'asc' ? -1 : 1;
                if (vb == null) return dir === 'asc' ? 1 : -1;

                const da = col === 'createdAt' ? new Date(va).getTime() : NaN;
                const db = col === 'createdAt' ? new Date(vb).getTime() : NaN;

                if (!isNaN(da) && !isNaN(db)) return dir === 'asc' ? da - db : db - da;

                const sa = String(va).toLowerCase();
                const sb = String(vb).toLowerCase();

                if (sa < sb) return dir === 'asc' ? -1 : 1;
                if (sa > sb) return dir === 'asc' ? 1 : -1;
                return 0;
            });
        }

        this.filteredLists = arr;
        this.selectedIds.clear();
        this.currentPage = 1;

        this.updatePagedLists();
        this.updateSummary();
    }

    isFirstRowDropdownOpen(): boolean {
        if (!this.pagedLists || this.pagedLists.length === 0) {
            return false;
        }

        return this.isDropdownOpen(this.pagedLists[0].loadingListId);
    }

    toggleDropdown(id: number, event: MouseEvent): void {
        event.stopPropagation();

        if (this.openDropdownId === id) {
            this.openDropdownId = null;
            return;
        }

        const button = event.currentTarget as HTMLElement;
        const rect = button.getBoundingClientRect();

        const menuWidth = 188;
        const menuHeight = 108;
        const gap = 6;

        let left = rect.right - menuWidth;
        let top = rect.top - menuHeight - gap;

        if (left < 8) {
            left = 8;
        }

        if (top < 8) {
            top = rect.bottom + gap;
        }

        this.dropdownStyles[id] = {
            top: `${top}px`,
            left: `${left}px`
        };

        this.openDropdownId = id;
    }

    getDropdownStyle(id: number): any {
        return this.dropdownStyles[id] || {};
    }

    confirmChangeTour(tourRoutingId: number, carrierCode: string | null = null): void {
        const selectedParcelIds: number[] = this.parcels
            .filter(p => this.selectedParcelIds.has(p.parcelComplementaryId))
            .map(p => Number(p.parcelComplementaryId));

        if (!selectedParcelIds.length) {
            this.notificationService.warning('Seleziona almeno un collo');
            return;
        }

        const payload: ChangeTourRequest = {
            comParcelsList: selectedParcelIds,
            tourRoutingId: Number(tourRoutingId),
            warehouseCode: this.appliedWarehouse,
            carrierCode: carrierCode || undefined
        };

        this.postWithTripConfirmation(
            `${environment.apiUrl}/change-tour/changeTour`,
            payload,
            'Tour cambiato con successo',
            'Errore durante il cambio tour');
    }
}